import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { CheckCircle, ArrowRight, Building2, Mail, Key } from 'lucide-react';
import { LanguageSwitcher } from '@/components/language-switcher';

export default function SignupSuccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(10);

  // Get session info from URL params (passed by Stripe redirect)
  const sessionId = searchParams.get('session_id');
  const subdomain = searchParams.get('subdomain');

  // Countdown and redirect
  useEffect(() => {
    if (countdown <= 0) {
      // Redirect to the tenant's subdomain login page
      if (subdomain) {
        window.location.href = `https://${subdomain}.thenotfit.com/login`;
      } else {
        navigate('/login');
      }
      return;
    }

    const timer = setTimeout(() => {
      setCountdown(countdown - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, subdomain, navigate]);

  const handleGoToLogin = () => {
    if (subdomain) {
      window.location.href = `https://${subdomain}.thenotfit.com/login`;
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8 px-4 flex items-center justify-center">
      {/* Language Switcher */}
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>

      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
          <CardTitle className="text-2xl">
            {t('tenantSignup.success.title')}
          </CardTitle>
          <CardDescription className="text-lg">
            {t('tenantSignup.success.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* What's been set up */}
          <div className="bg-muted p-4 rounded-lg space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {t('tenantSignup.success.whatSetUp')}
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {t('tenantSignup.success.databaseCreated')}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {t('tenantSignup.success.adminAccountCreated')}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {t('tenantSignup.success.subdomainConfigured')}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {t('tenantSignup.success.subscriptionActivated')}
              </li>
            </ul>
          </div>

          {/* Login details */}
          {subdomain && (
            <div className="bg-primary/10 p-4 rounded-lg space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Key className="h-5 w-5" />
                {t('tenantSignup.success.loginDetails')}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t('tenantSignup.success.yourDashboard')}
                  </span>
                  <a
                    href={`https://${subdomain}.thenotfit.com`}
                    className="text-primary hover:underline font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {subdomain}.thenotfit.com
                  </a>
                </div>
                <p className="text-muted-foreground">
                  {t('tenantSignup.success.useCredentials')}
                </p>
              </div>
            </div>
          )}

          {/* Email notification */}
          <div className="flex items-start gap-3 text-sm text-muted-foreground">
            <Mail className="h-5 w-5 mt-0.5 flex-shrink-0" />
            <p>{t('tenantSignup.success.emailSent')}</p>
          </div>

          {/* Action buttons */}
          <div className="space-y-3">
            <Button className="w-full" size="lg" onClick={handleGoToLogin}>
              {t('tenantSignup.success.goToDashboard')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              {t('tenantSignup.success.redirecting', { seconds: countdown })}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
